import * as Accountant from 'app/calcs/Accountant'
import { Local701PensionCalculations } from 'app/calcs/customers/local701'
import { ILocal701PensionInputs, Local701FormOfPayment } from 'app/calcs/customers/local701/interfaces'
import { FormOfPaymentOptions } from 'app/calcs/pensions'
import { AllFormsOfPayment, IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { MaterialInput, RadioGroup } from 'app/components'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import MaterialNumber from 'app/components/material/MaterialNumber';
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { dateAtAge, IRetirementPerson, isSamePersonForSSA, Profile } from 'app/redux/Profile/model'
import { theCensusInfo } from 'app/services/models/PublicCensusInfo'
import { WorkbookServiceResponse } from 'app/services/models/Workbook'
import * as utils from 'app/utils'
import { RequestCache } from 'app/utils/RequestCache'
import { getBrandHelpers } from 'config'
import { fundOfficeEmployee } from 'config/701/ETL/interfaces'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, ButtonGroup, Col, FormGroup, ListGroup, ListGroupItem, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'

interface ILocal74PensionContainerState extends IProfileContainerState {
  headOfHousehold: IRetirementPerson
  monthlyPayout: string

  // these are synced with profile.pension.local701
  beginPaymentsAge: moment.Duration
  formOfPayment: Local701FormOfPayment
  hoursWorked: number
  payRate: number,
  contributionRate: string
}

class Local74PensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, ILocal74PensionContainerState> {
  beginPaymentsAgeNS = 'pension.local701.beginPaymentsAge'
  hoursWorkedNS = 'pension.shared.hoursWorked'
  payRateNS = 'pension.local701.payRate'
  formOfPaymentNS = 'pension.local701.formOfPayment'
  contributionRateNS = 'pension.local701.contributionRate'

  state: ILocal74PensionContainerState = {
    profile: null,
    headOfHousehold: null,
    formOfPayment: this.getValueForQuestion(this.formOfPaymentNS) || SimplePensionFormOfPayment.singleLifeOnly,
    payRate: this.getValueForQuestion(this.payRateNS) || 0,
    hoursWorked: this.getValueForQuestion(this.hoursWorkedNS) || 0,
    monthlyPayout: '$0',
    beginPaymentsAge: this.getValueForQuestion(this.beginPaymentsAgeNS) || null,
    contributionRate: this.getValueForQuestion(this.contributionRateNS) || fundOfficeEmployee.no
  }

  initCalc = false

  debounceCalculations = _.debounce(() => {
    // tslint:disable-next-line:no-console
    //console.log('local701.debounceCalculations entered')
    const person = this.state.headOfHousehold
    const profile = this.state.profile
    const inputs = this.state.profile.pension.local701
    const { payRate, beginPaymentsAge, formOfPayment, contributionRate } = this.state;
    const when = dateAtAge(person, beginPaymentsAge)

    const computer = new Local701PensionCalculations(this.state.profile)
    computer.setSpouse(profile.getSpouse())
    computer.getMonthlyPensionAmount(person, inputs, when)
    .then((payout) => {
      // tslint:disable-next-line:no-debugger
      //debugger
      const monthlyPayout = utils.toCurrency(payout.amount)
      this.setState({ monthlyPayout })
    })

  }, 300)

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info')
      const headOfHousehold = profile.getHeadOfHousehold()

      if (profile.pension.local701) {
        this.debounceCalculations();
      }

      this.setState({ headOfHousehold, profile })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: ILocal74PensionContainerState) {
    this.updateCalculations(nextState)
  }

  updateCalculations(nextState: ILocal74PensionContainerState) {
    const { profile, formOfPayment, headOfHousehold, hoursWorked, payRate, beginPaymentsAge, contributionRate } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(headOfHousehold.selectedRetirementAge, 'years')

    const updates = {
      beginPaymentsAge,
      formOfPayment,
      hoursWorked,
      payRate,
      contributionRate: this.toNumber(contributionRate)
    }
    const newLocal701 = _.defaults({}, updates, profile.pension.local701)

    if (!this.initCalc || !_.isEqual(profile.pension.local701, newLocal701)) {
      this.initCalc = true
      profile.pension.local701 = newLocal701
      this.debounceCalculations()
    }
  }

  updateHoursWorked(hoursWorked: number) {
    this.state.profile.pension.shared.hoursWorked = hoursWorked
    this.setStateAndSave({ hoursWorked })
  }

  updatePaymentsAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.local701.beginPaymentsAge = beginPaymentsAge
    this.setState({ beginPaymentsAge })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  render() {
    if (!this.state.headOfHousehold) { return null }
    if (!this.state.profile.pension || !this.state.profile.pension.local701 || !this.state.beginPaymentsAge) { return null }

    const censusDateText = theCensusInfo.censusDate.format('LL')

    const profile = this.state.profile
    const spouseIsValid = profile.spouseIsValid()
    const local701 = profile.pension.local701

    const currentFormOfPayment = FormOfPaymentOptions
      .find((fopo) => fopo.value === this.state.formOfPayment)
      .label

    const beginPaymentsAgeText = utils.toNiceAge(this.state.beginPaymentsAge)
    const showSummary = this.state.monthlyPayout !== '$0'

    const findFop = (e: AllFormsOfPayment) => _.some(FormOfPaymentOptions, (q) => q.value === e && (spouseIsValid || !q.needsSpouse));
    const allowedFormsOfPayment = _.filter(getBrandHelpers().memberPensionFormsOfPayment, (e) => findFop(e))

    const aStyle = {
      marginBottom: '10px'
    }

    return (
      <section className="animated fadeIn">
        <h1>Local 74 Pension</h1>
        <h5 className="lead" style={aStyle}>
          We have the following information from your records. This information is accurate
          as of {censusDateText}. {theCensusInfo.publicNotes}
        </h5>
        <ul >
          <li>Local 74 Benefit Service: <a>{utils.toNiceAge(local701.futureService, { yearsUnits: ' years' })}</a></li>
          <li>Local 74 Accrued Benefit: <a>{utils.toCurrency(local701.monthlyBenefit)}</a></li>
        </ul>

        <Row>
          <Col md={6}>

            <FormGroup>
              <MaterialNumber
                placeholder="How many hours per year will you work?"
                formattedNumberMode
                name={this.hoursWorkedNS}
                onChange={(e) => this.updateHoursWorked(this.toNumber(e.currentTarget.value))}
                value={this.state.hoursWorked} />
            </FormGroup>

            <FormGroup>
              <MaterialInput
                placeholder="What is your current contribution rate?"
                name={this.contributionRateNS}
                floatMode
                onChange={(e) => this.setStateAndSave({ contributionRate: e.currentTarget.value })}
                value={this.state.contributionRate} />
            </FormGroup>

            <FormGroup>
              <RetirementAgeSelector min={55} max={70}
                placeholder="What age will you begin payments?"
                selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updatePaymentsAge(a)} />
            </FormGroup>

            <FormGroup>
              <FormOfPayment name={this.formOfPaymentNS}
                defaultValue={this.state.formOfPayment}
                options={allowedFormsOfPayment}
                onChange={(formOfPayment) => this.setStateAndSave({ formOfPayment })} />
            </FormGroup>

          </Col>
          <Col md={6}>
            {showSummary && <h2 className="animated fadeIn">
              If {this.state.headOfHousehold.firstName} begins to take payments at age&nbsp;
              <a>{beginPaymentsAgeText}</a>&nbsp;
              in the form of&nbsp;
              <a>{currentFormOfPayment}</a>&nbsp;
              the monthly amount will be <a>{this.state.monthlyPayout}</a>.&nbsp;
            </h2>}

          </Col>
        </Row>
      </section >
    )
  }
}

const Local74PensionQuestionsGroup = () => (
  <Local74PensionContainer
    header="Local 74 Pension" icon="" />
)

export {
    Local74PensionQuestionsGroup,
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/pension/Local74/index.tsx